import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchProjects,
  fetchProjectDevelopers,
  fetchCountries,
} from "@/services/api";
import { useSearchParams } from "react-router-dom";
import { FeatureCollection } from "geojson";
import { ProjectsResponse } from "@/types";

export const useProjects = (
  itemsPerPage: number = 500,
  options?: { ignoreFilters?: boolean }
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Math.max(1, Number(searchParams.get("page")) || 1);

  const [pagination, setPagination] = useState({
    page: initialPage,
    limit: itemsPerPage,
    offset: Math.max(0, (initialPage - 1) * itemsPerPage),
  });

  const [projectDeveloperId, setProjectDeveloperId] = useState<
    string | undefined
  >(
    options?.ignoreFilters
      ? undefined
      : searchParams.get("developer") || undefined
  );

  const [countryCode, setCountryCode] = useState<string | undefined>(
    options?.ignoreFilters
      ? undefined
      : searchParams.get("country") || undefined
  );

  const [bbox, setBbox] = useState<string | undefined>(
    searchParams.get("bbox") || undefined
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      "projects",
      pagination.limit,
      pagination.offset,
      options?.ignoreFilters ? undefined : projectDeveloperId,
      options?.ignoreFilters ? undefined : countryCode,
      bbox,
    ],
    queryFn: () => {
      return fetchProjects(
        pagination.limit,
        pagination.offset,
        options?.ignoreFilters ? undefined : projectDeveloperId,
        options?.ignoreFilters ? undefined : countryCode,
        bbox
      );
    },
  });

  const { data: projectDevelopers, isLoading: isLoadingDevelopers } = useQuery({
    queryKey: ["projectDevelopers"],
    queryFn: () => fetchProjectDevelopers(1000),
  });

  const { data: countries, isLoading: isLoadingCountries } = useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.page > 1) {
      params.set("page", pagination.page.toString());
    }
    if (projectDeveloperId) {
      params.set("developer", projectDeveloperId);
    }
    if (countryCode) {
      params.set("country", countryCode);
    }
    if (bbox) {
      params.set("bbox", bbox);
    }
    setSearchParams(params, { replace: true });
  }, [pagination.page, projectDeveloperId, countryCode, bbox, setSearchParams]);

  // Handle page changes with offset validation
  const goToPage = useCallback(
    (page: number) => {
      // Ensure page is at least 1
      const validPage = Math.max(1, page);
      const newOffset = Math.max(0, (validPage - 1) * pagination.limit);

      setPagination((prev) => ({
        ...prev,
        page: validPage,
        offset: newOffset,
      }));
    },
    [pagination.limit]
  );

  // Handle project developer filter change
  const handleDeveloperChange = useCallback(
    (developerId: string | undefined) => {
      setProjectDeveloperId(developerId);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        offset: 0,
      }));
    },
    []
  );

  const handleCountryChange = useCallback((country: string | undefined) => {
    setCountryCode(country);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      offset: 0,
    }));
  }, []);

  const handleBboxChange = useCallback((newBbox: string | undefined) => {
    setBbox(newBbox);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      offset: 0,
    }));
  }, []);

  const isProjectsResponse = (response: any): response is ProjectsResponse => {
    return response && "data" in response && "meta" in response;
  };

  return {
    projects: isProjectsResponse(data)
      ? data.data.filter((project) => project.isPublic !== false)
      : [],
    meta: isProjectsResponse(data) ? data.meta : undefined,
    isLoading,
    isError,
    pagination,
    goToPage,
    refetch,
    projectDevelopers: projectDevelopers || [],
    isLoadingDevelopers,
    projectDeveloperId,
    handleDeveloperChange,
    countries: countries || [],
    isLoadingCountries,
    countryCode,
    handleCountryChange,
    bbox,
    handleBboxChange,
    featureCollection: !isProjectsResponse(data)
      ? (data as FeatureCollection)
      : undefined,
  };
};
